import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import {
    ArrowRight, ArrowUpRight, Rocket, Sparkles, Target,
    Users, GraduationCap, Award, LineChart, BookOpen,
    Layers, ShieldCheck, Cpu, Compass, Wrench, Building2,
    Globe2, Boxes, Clock, TrendingUp, CheckCircle2,
    Landmark, HeartPulse, ShoppingBag, Factory, RadioTower,
    Zap, Briefcase, Download, ChevronDown, Search, Plus, Minus,
} from "lucide-react";
import { SOLUTIONS_TRAINING, SOLUTIONS_TALENT, IMAGES, PARTNER_LOGOS } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";
import { Link } from "react-router-dom";

/* ---------- Solutions benefits (existing) ---------- */
const SOLUTION_BENEFITS = {
    "corporate-training": [
        { icon: Target, label: "Business-aligned learning strategy" },
        { icon: Layers, label: "Blended live + self-paced + labs" },
        { icon: LineChart, label: "Capability uplift analytics" },
        { icon: Globe2, label: "Multi-region, multi-language delivery" },
        { icon: Users, label: "Role-anchored cohorts" },
        { icon: Award, label: "OEM-aligned certifications" },
    ],
    "new-hire-training": [
        { icon: Rocket, label: "12–16 week bootcamp academies" },
        { icon: Boxes, label: "Real project simulations" },
        { icon: Users, label: "Buddy + mentor fabric" },
        { icon: Clock, label: "Up to 60% faster ramp-time" },
        { icon: CheckCircle2, label: "Readiness gates, not attendance" },
        { icon: GraduationCap, label: "Campus-to-corporate ready" },
    ],
    "certifications": [
        { icon: Award, label: "25+ global certifying bodies" },
        { icon: BookOpen, label: "Official-blueprint aligned prep" },
        { icon: ShieldCheck, label: "AI-proctored exam integrity" },
        { icon: Sparkles, label: "Diagnostic mocks + coaching" },
        { icon: Compass, label: "End-to-end voucher management" },
        { icon: TrendingUp, label: "Pass rates above global averages" },
    ],
    "managed-ld": [
        { icon: Wrench, label: "Fully outsourced L&D operations" },
        { icon: LineChart, label: "SLA-driven learning outcomes" },
        { icon: Layers, label: "Platform + content + delivery" },
        { icon: Building2, label: "Dedicated program managers" },
        { icon: Target, label: "Board-visible KPIs" },
        { icon: ShieldCheck, label: "One accountable partner" },
    ],
    "hire-train-deploy": [
        { icon: Users, label: "Custom-archetype sourcing" },
        { icon: Cpu, label: "Stack-specific training" },
        { icon: Rocket, label: "Deploy-ready in 12–16 weeks" },
        { icon: CheckCircle2, label: "Role-fit assessments" },
        { icon: TrendingUp, label: "Higher retention via career paths" },
        { icon: ShieldCheck, label: "Performance-SLA backed" },
    ],
    "proctored-assessments": [
        { icon: ShieldCheck, label: "Biometric identity assurance" },
        { icon: Globe2, label: "Global remote reach" },
        { icon: Sparkles, label: "AI anomaly detection" },
        { icon: BookOpen, label: "Immutable audit trail" },
        { icon: CheckCircle2, label: "Bias & fairness guardrails" },
        { icon: LineChart, label: "Scores mapped to competencies" },
    ],
    "niche-skill-projects": [
        { icon: Cpu, label: "Vetted GenAI / cloud specialists" },
        { icon: Target, label: "Outcome-scoped engagements" },
        { icon: Boxes, label: "Playbooks + knowledge transfer" },
        { icon: Clock, label: "Kickoff within days" },
        { icon: TrendingUp, label: "Skip specialist-hiring cycles" },
        { icon: ShieldCheck, label: "De-risked niche delivery" },
    ],
};

// SEO-friendly keyword paragraph for each solution (used inside sections)
const SOLUTION_SEO = {
    "corporate-training": "Enterprise corporate training programs from SpringXE combine strategy alignment, blended delivery, and outcome analytics — designed for L&D leaders scaling GenAI, Cloud, Cybersecurity, and Data capability across global cohorts.",
    "new-hire-training": "Structured new-hire training academies accelerate campus-to-corporate ramp with role simulations, bootcamps, and mentor fabrics — a proven route to reduce onboarding time and lift graduate-cohort productivity in the first 90 days.",
    "certifications": "SpringXE-managed certification programs are aligned to 25+ official OEM blueprints — from AWS and Azure to CISSP, ISTQB, and Scrum — with AI-proctored delivery, readiness coaching, and full voucher administration.",
    "managed-ld": "Outsource strategy, content, delivery, and reporting under one SLA-backed engagement. SpringXE Managed L&D consolidates learning operations onto a single enterprise LxP with board-visible KPIs and dedicated program governance.",
    "hire-train-deploy": "Hire–Train–Deploy talent pipelines source, upskill, and deploy engineers on your exact tech stack — a proven answer to niche-skill shortages in GenAI, Cloud, Cybersecurity, Data, and SAP.",
    "proctored-assessments": "AI-proctored, biometrically verified assessments preserve integrity for enterprise hiring, promotion, and certification decisions — with audit-ready records and bias-safe scoring across global geographies.",
    "niche-skill-projects": "On-demand access to vetted GenAI, cloud, and cybersecurity specialists for outcome-scoped enterprise projects — with productization, playbooks, and knowledge transfer built in.",
};

const SOLUTIONS = [
    ...SOLUTIONS_TRAINING, ...SOLUTIONS_TALENT,
].map((s) => ({ ...s, benefits: SOLUTION_BENEFITS[s.slug] || [], seo: SOLUTION_SEO[s.slug] || "" }));

/* ---------- Impact metrics ---------- */
const METRICS = [
    { label: "Enterprise Customers", value: 500, suffix: "+" },
    { label: "Training Programs", value: 1000, suffix: "+" },
    { label: "Learners Trained", value: 1000000, suffix: "+", display: (n) => `${(n / 1000000).toFixed(1)}M` },
    { label: "Expert Trainers", value: 250, suffix: "+" },
    { label: "Global Technology Partners", value: 30, suffix: "+" },
];

const AnimatedCounter = ({ to, suffix = "", display }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return undefined;
        const controls = animate(0, to, {
            duration: 2.2, ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setN(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);
    return (
        <span ref={ref}>
            {display ? display(n) : n.toLocaleString()}{suffix}
        </span>
    );
};

/* ---------- Process timeline ---------- */
const PROCESS = [
    { step: "01", title: "Understand Business Goals", icon: Target },
    { step: "02", title: "Assess Skill Gaps", icon: Search },
    { step: "03", title: "Design Learning Strategy", icon: Compass },
    { step: "04", title: "Deliver Training", icon: GraduationCap },
    { step: "05", title: "Measure Outcomes", icon: LineChart },
    { step: "06", title: "Continuous Improvement", icon: TrendingUp },
];

/* ---------- Industries ---------- */
const INDUSTRY_ICONS = {
    "Banking & Financial Services": Landmark,
    "IT & Software": Cpu,
    "Manufacturing": Factory,
    "Healthcare": HeartPulse,
    "Retail": ShoppingBag,
    "Telecom": RadioTower,
    "Energy": Zap,
    "Government": Building2,
};
const INDUSTRIES = Object.keys(INDUSTRY_ICONS);

/* ---------- Testimonials ---------- */
const TESTIMONIALS = [
    { quote: "SpringXE transformed our workforce upskilling initiative and reduced onboarding time by 40%.", name: "Ananya R.", role: "Chief Learning Officer", company: "Global BFSI Leader" },
    { quote: "Their Hire-Train-Deploy pods filled 120+ niche cloud roles inside a single quarter — with SLA-backed retention.", name: "Vikram S.", role: "VP Talent Development", company: "Fortune 500 Technology" },
    { quote: "The proctored-assessments platform gave us the integrity signal we could confidently defend to regulators.", name: "Priya M.", role: "Director HR", company: "Manufacturing Conglomerate" },
];

/* ---------- Trusted partners ---------- */
const PARTNER_SHOWCASE = [
    "aws", "microsoft", "sap", "gcp", "redhat", "github", "pmi",
    // "comptia", // hidden — re-add to restore CompTIA in this showcase
    // "isc2", // hidden — re-add to restore ISC2 in this showcase
];

/* ---------- FAQs ---------- */
const FAQS = [
    { q: "Which enterprise learning solutions does SpringXE offer?", a: "SpringXE offers seven flagship solutions — Corporate Training, New Hire Training, Certifications, Managed L&D, Hire–Train–Deploy, Proctored Assessments, and Niche Skill Projects — all delivered on our AI-native Enterprise LxP." },
    { q: "How does SpringXE measure learning outcomes?", a: "Every program is anchored to a live enterprise skills graph. We instrument capability uplift, project readiness, and business KPIs — not just completion rates — with board-visible dashboards." },
    { q: "Can SpringXE deliver globally?", a: "Yes. We deliver programs across 45+ countries with multi-region, multi-language capability, dedicated program managers, and enterprise-grade governance (SOC 2 aligned, GDPR & DPDP compliant)." },
    { q: "How quickly can a Hire–Train–Deploy pipeline be spun up?", a: "Custom talent pipelines typically go from kickoff to deploy-ready engineers in 12–16 weeks, backed by SLA guarantees against role-fit and retention." },
    { q: "Which technology partnerships underpin SpringXE certifications?", a: "SpringXE certifications are officially aligned to 18+ global bodies including AWS, Microsoft, Google Cloud, SAP, Red Hat, ISC2, CompTIA, ISTQB, and PMI." },
];

/* ============================================================= */
/*                           SECTIONS                             */
/* ============================================================= */

/* Hero */
const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
    return (
        <section ref={ref} id="top" className="relative pt-32 md:pt-40 pb-24 overflow-hidden" data-testid="solutions-hero">
            <BgLayer variant="mesh" intensity="strong" />
            <div className="sxe-container relative grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <div className="overline"><span className="dot" /> Enterprise Solutions</div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-6">
                        Enterprise Solutions That <span className="text-gradient">Build Skills, Scale Talent &amp; Transform Businesses</span>
                    </h1>
                    <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-2xl">
                        From enterprise learning and workforce transformation to talent acquisition and managed learning services, SpringXE helps organizations build future-ready teams with scalable, outcome-driven solutions.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <a href="#solutions" className="btn-primary" data-testid="hero-cta-explore">
                            Explore Solutions <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href="#final-cta" className="btn-secondary" data-testid="hero-cta-expert">Talk to an Expert</a>
                    </div>
                </motion.div>

                <motion.div style={{ y }} className="relative">
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25">
                        <img src={IMAGES.enterpriseTeam} alt="Enterprise workforce transformation" className="w-full h-[460px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#07020F]/50 via-transparent to-[#A100FF]/15" />
                    </motion.div>
                    {[
                        { icon: Sparkles, text: "AI-native LxP", pos: "-top-4 -left-4", delay: 0 },
                        { icon: TrendingUp, text: "+38% skill velocity", pos: "-bottom-4 -right-4", delay: 0.5 },
                        { icon: Globe2, text: "45+ countries", pos: "top-1/2 -right-6", delay: 1 },
                    ].map((p) => {
                        const Icon = p.icon;
                        return (
                            <motion.div key={p.text} animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: p.delay }}
                                className={`absolute ${p.pos} flex items-center gap-2 rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/15 px-4 py-2.5 shadow-2xl`}>
                                <Icon className="w-4 h-4 text-[#C266FF]" />
                                <span className="font-display text-white text-[13.5px]">{p.text}</span>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
};

/* Scroll Progress bar (top of viewport) */
const ScrollProgress = () => {
    const { scrollYProgress } = useScroll();
    return (
        <motion.div
            style={{ scaleX: scrollYProgress }}
            className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#A100FF] via-[#C266FF] to-[#7B00CC] origin-left z-[60]"
        />
    );
};

/* Sticky In-Page Tabs */
const StickyTabs = ({ solutions, activeSlug }) => (
    <div className="sticky top-[72px] z-30 backdrop-blur-xl bg-[#07020F]/85 border-y border-white/10" data-testid="solutions-sticky-nav">
        <div className="sxe-container">
            <nav className="flex items-center gap-1 py-3 overflow-x-auto no-scrollbar">
                {solutions.map((s) => {
                    const isActive = activeSlug === s.slug;
                    return (
                        <a
                            key={s.slug}
                            href={`#${s.slug}`}
                            data-testid={`sticky-tab-${s.slug}`}
                            className={`px-4 py-2 rounded-full whitespace-nowrap font-display text-[13.5px] font-medium tracking-tight transition-all ${
                                isActive
                                    ? "bg-gradient-to-r from-[#A100FF] to-[#7B00CC] text-white shadow-[0_8px_20px_-10px_rgba(161,0,255,0.6)]"
                                    : "text-[#B0A3C4] hover:text-white hover:bg-white/[0.05]"
                            }`}
                        >
                            {s.title}
                        </a>
                    );
                })}
            </nav>
        </div>
    </div>
);

/* Impact at a Glance */
const ImpactMetrics = () => (
    <section className="relative py-16 md:py-20 overflow-hidden" data-testid="impact-metrics-section">
        <BgLayer variant="orbs" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Impact at a Glance</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    A decade of <span className="text-gradient">enterprise learning impact</span>.
                </h2>
            </div>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {METRICS.map((m, i) => (
                    <motion.div
                        key={m.label}
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5, delay: i * 0.06 }}
                        className="relative rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] overflow-hidden hover:border-[#C266FF]/40 transition-colors"
                        data-testid={`impact-metric-${i}`}
                    >
                        <div className="font-display text-4xl lg:text-5xl font-medium text-white tracking-tighter">
                            <AnimatedCounter to={m.value} suffix={m.suffix} display={m.display} />
                        </div>
                        <div className="text-[12px] uppercase tracking-widest text-[#B0A3C4] font-display mt-2 leading-snug">
                            {m.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

/* Process Timeline */
const ProcessTimeline = () => (
    <section className="relative py-20 md:py-24 overflow-hidden bg-[#08031a]" data-testid="process-timeline-section">
        <BgLayer variant="dots" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> How We Deliver Results</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    A six-step delivery model, <span className="text-gradient">measurable at every stage</span>.
                </h2>
            </div>

            <div className="relative mt-14">
                <div className="absolute left-0 right-0 top-6 h-px bg-gradient-to-r from-transparent via-[#C266FF]/40 to-transparent hidden lg:block" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                    {PROCESS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.step}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative"
                                data-testid={`process-step-${p.step}`}
                            >
                                <div className="relative z-[1] w-12 h-12 rounded-full bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(161,0,255,0.7)]">
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <div className="mt-5">
                                    <div className="text-[11px] uppercase tracking-widest text-[#C266FF] font-display">Step {p.step}</div>
                                    <div className="font-display text-white text-[16px] font-semibold tracking-tight leading-snug mt-1.5">
                                        {p.title}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    </section>
);

/* Industries Coverage */
const IndustriesSection = () => (
    <section className="relative py-20 md:py-24 overflow-hidden" data-testid="industries-section">
        <BgLayer variant="mesh" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Industries We Serve</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Impact across <span className="text-gradient">every enterprise vertical</span>.
                </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
                {INDUSTRIES.map((name, i) => {
                    const Icon = INDUSTRY_ICONS[name] || Briefcase;
                    return (
                        <motion.div
                            key={name}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#C266FF]/40 hover:bg-white/[0.05] hover:-translate-y-1 transition-all duration-400"
                            data-testid={`industry-card-${name.split(" ")[0].toLowerCase()}`}
                        >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center group-hover:scale-105 transition-transform">
                                <Icon className="w-5 h-5 text-[#E9CCFF]" />
                            </div>
                            <div className="mt-4 font-display text-white text-[15.5px] font-semibold tracking-tight">
                                {name}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);

/* Solutions intro */
const SolutionsIntro = () => (
    <section className="relative pt-4 pb-6" id="solutions">
        <div className="sxe-container">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl"
            >
                <div className="overline"><span className="dot" /> Solutions Overview</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Seven capability engagements. <span className="text-gradient">One accountable partner.</span>
                </h2>
                <p className="text-[#B0A3C4] text-lg leading-relaxed mt-5">
                    Scroll through our end-to-end enterprise learning &amp; talent solutions — each measurable, outcome-anchored, and delivered on the SpringXE platform.
                </p>
            </motion.div>
        </div>
    </section>
);

/* Zig-zag Solution Section (richer hover) */
const SolutionSection = ({ solution, index, setActive }) => {
    const isImageRight = index % 2 === 0;
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-40% 0px -40% 0px" });
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imageY = useTransform(scrollYProgress, [0, 1], [40, -40]);

    useEffect(() => {
        if (inView) setActive(solution.slug);
    }, [inView, solution.slug, setActive]);

    const contentSlide = isImageRight ? -50 : 50;
    const imageSlide = isImageRight ? 60 : -60;
    const isAlt = index % 2 === 1;

    return (
        <section
            id={solution.slug}
            ref={ref}
            className={`relative py-20 md:py-28 overflow-hidden ${isAlt ? "bg-[#08031a]" : ""} scroll-mt-32`}
            data-testid={`solution-section-${solution.slug}`}
        >
            {index % 3 === 1 && <BgLayer variant="dots" />}
            {index % 3 === 2 && <BgLayer variant="orbs" />}

            <div className="sxe-container relative">
                <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isImageRight ? "" : "lg:[&>*:first-child]:order-2"}`}>
                    {/* Content */}
                    <motion.div
                        initial={{ opacity: 0, x: contentSlide }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="overline"><span className="dot" /> Solution {String(index + 1).padStart(2, "0")}</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-[52px] font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            {solution.title}
                        </h2>
                        <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6 max-w-xl">{solution.description}</p>
                        <p className="text-[#8E7EA8] text-[14.5px] leading-relaxed mt-4 max-w-xl">{solution.seo}</p>

                        <div className="mt-8 grid sm:grid-cols-2 gap-3">
                            {solution.benefits.map((b, i) => {
                                const Icon = b.icon;
                                return (
                                    <motion.div
                                        key={b.label}
                                        initial={{ opacity: 0, y: 14 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-40px" }}
                                        transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
                                        className="group flex items-start gap-3 p-3.5 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:border-[#C266FF]/40 hover:bg-white/[0.04] transition-all"
                                        data-testid={`solution-benefit-${solution.slug}-${i}`}
                                    >
                                        <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform">
                                            <Icon className="w-4 h-4 text-[#E9CCFF]" />
                                        </div>
                                        <span className="font-display text-[13.5px] leading-snug text-[#E9CCFF] pt-1.5">
                                            {b.label}
                                        </span>
                                    </motion.div>
                                );
                            })}
                        </div>

                        <div className="mt-8">
                            <Link
                                to={`/solutions/${solution.slug}`}
                                className="btn-primary group"
                                data-testid={`solution-cta-${solution.slug}`}
                            >
                                Learn More <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </Link>
                        </div>
                    </motion.div>

                    {/* Image with rich hover */}
                    <motion.div
                        initial={{ opacity: 0, x: imageSlide }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                        style={{ y: imageY }}
                        className="relative group"
                    >
                        <div className="relative rounded-3xl overflow-hidden border border-[#C266FF]/20 shadow-[0_30px_60px_-30px_rgba(161,0,255,0.4)]">
                            <img
                                src={solution.image}
                                alt={solution.title}
                                loading="lazy"
                                className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07020F]/60 via-transparent to-[#A100FF]/10" />
                            {/* Rich hover gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#A100FF]/0 via-transparent to-[#7B00CC]/0 group-hover:from-[#A100FF]/35 group-hover:to-[#7B00CC]/25 transition-all duration-700" />

                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute top-5 left-5 flex items-center gap-2 rounded-full bg-white/[0.08] backdrop-blur-xl border border-white/15 px-3.5 py-1.5"
                            >
                                <span className="dot" />
                                <span className="font-display text-[11px] uppercase tracking-widest text-white">
                                    {solution.title.split(" ")[0]}
                                </span>
                            </motion.div>

                            {/* Floating accent - appears on hover */}
                            <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                                <div className="rounded-full bg-white text-[#0E1220] font-display font-semibold text-[13px] px-4 py-2 shadow-2xl inline-flex items-center gap-1.5">
                                    Explore <ArrowUpRight className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

/* Testimonial banner (between solution pairs) */
const TestimonialBanner = ({ t, i }) => (
    <section className="relative py-14 md:py-16 overflow-hidden" data-testid={`testimonial-${i}`}>
        <BgLayer variant="mesh" />
        <div className="sxe-container relative">
            <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
                className="relative rounded-3xl border border-[#C266FF]/20 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-8 md:p-10"
            >
                <div className="absolute -top-4 left-8 font-display text-[110px] leading-none text-[#A100FF]/25 select-none">“</div>
                <p className="relative font-display text-white text-2xl md:text-3xl leading-[1.3] tracking-tight max-w-4xl">
                    {t.quote}
                </p>
                <footer className="mt-6 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center font-display font-semibold text-white">
                        {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                        <div className="font-display text-white font-semibold text-[15px]">{t.name}</div>
                        <div className="text-[12px] text-[#B0A3C4] font-display tracking-tight">
                            {t.role} · <span className="text-[#E9CCFF]">{t.company}</span>
                        </div>
                    </div>
                </footer>
            </motion.blockquote>
        </div>
    </section>
);

/* Trusted Partners */
const TrustedPartners = () => (
    <section className="relative py-20 md:py-24 overflow-hidden bg-[#08031a]" data-testid="trusted-partners-section">
        <BgLayer variant="dots" />
        <div className="sxe-container relative">
            <div className="max-w-3xl">
                <div className="overline"><span className="dot" /> Trusted Learning &amp; Technology Partners</div>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                    Officially aligned with the <span className="text-gradient">world&rsquo;s technology leaders</span>.
                </h2>
            </div>
            <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-9 gap-4">
                {PARTNER_SHOWCASE.map((slug, i) => {
                    const src = PARTNER_LOGOS[slug];
                    return (
                        <motion.div
                            key={slug}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.35, delay: i * 0.04 }}
                            className="rounded-xl bg-white h-20 flex items-center justify-center px-3 shadow-[0_8px_20px_-12px_rgba(9,3,20,0.6)] hover:shadow-[0_16px_36px_-16px_rgba(161,0,255,0.35)] hover:-translate-y-0.5 transition-all duration-400"
                            data-testid={`trusted-partner-${slug}`}
                        >
                            {src && (
                                <img
                                    src={src}
                                    alt={slug}
                                    className="max-h-8 max-w-[80%] object-contain filter grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            )}
                        </motion.div>
                    );
                })}
            </div>
            <div className="mt-8 text-center">
                <Link to="/partners" className="btn-secondary" data-testid="trusted-partners-cta">
                    View all partners <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    </section>
);

/* FAQ */
const FAQItem = ({ item, i, open, onToggle }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.4, delay: i * 0.05 }}
        className="rounded-2xl border border-white/10 bg-white/[0.03]"
        data-testid={`faq-item-${i}`}
    >
        <button
            type="button"
            onClick={onToggle}
            className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
            aria-expanded={open}
        >
            <span className="font-display text-white text-[16.5px] font-semibold tracking-tight">{item.q}</span>
            <span className="shrink-0 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-[#E9CCFF]">
                {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </span>
        </button>
        {open && (
            <div className="px-6 pb-5 text-[#B0A3C4] text-[14.5px] leading-relaxed">
                {item.a}
            </div>
        )}
    </motion.div>
);

const FAQSection = () => {
    const [openIdx, setOpenIdx] = useState(0);
    return (
        <section className="relative py-20 md:py-24 overflow-hidden" data-testid="solutions-faq-section">
            <BgLayer variant="dots" />
            <div className="sxe-container relative max-w-4xl">
                <div className="text-center">
                    <div className="overline justify-center inline-flex"><span className="dot" /> Frequently asked</div>
                    <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                        Enterprise leaders <span className="text-gradient">frequently ask</span>.
                    </h2>
                </div>
                <div className="mt-10 space-y-3">
                    {FAQS.map((f, i) => (
                        <FAQItem
                            key={f.q}
                            item={f}
                            i={i}
                            open={openIdx === i}
                            onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

/* Final Premium CTA */
const FinalCTA = () => (
    <section id="final-cta" className="relative py-24 md:py-32 overflow-hidden" data-testid="solutions-final-cta">
        <BgLayer variant="mesh" intensity="strong" />
        <div className="sxe-container relative">
            <motion.div
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65 }}
                className="relative rounded-3xl overflow-hidden border border-[#C266FF]/25 bg-gradient-to-br from-[#A100FF]/12 to-[#7B00CC]/[0.05]"
            >
                <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#A100FF]/20 blur-3xl" />
                <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-[#7B00CC]/25 blur-3xl" />

                <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center p-10 md:p-16">
                    <div>
                        <div className="overline mb-4"><span className="dot" /> Get Started</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.04] text-white">
                            Ready to <span className="text-gradient">Transform Your Workforce</span>?
                        </h2>
                        <p className="text-[#E9CCFF] text-lg leading-relaxed mt-6 max-w-xl">
                            Let&rsquo;s build a customized learning and talent strategy that aligns with your business goals.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a href="#top" className="btn-primary" data-testid="final-cta-consultation">
                                <Rocket className="w-4 h-4" /> Schedule a Consultation
                            </a>
                            <button
                                type="button"
                                onClick={() => { if (window.__openCatalogue) window.__openCatalogue(); }}
                                className="btn-secondary"
                                data-testid="final-cta-profile"
                            >
                                <Download className="w-4 h-4" /> Download Company Profile
                            </button>
                        </div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25"
                    >
                        <img src={IMAGES.enterpriseTeam} alt="Enterprise workforce consultation" className="w-full h-[360px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F]/50 via-transparent to-transparent" />
                    </motion.div>
                </div>
            </motion.div>
        </div>
    </section>
);

/* Schema markup (FAQ + Service) */
const SchemaMarkup = () => {
    const data = useMemo(() => ({
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "FAQPage",
                "mainEntity": FAQS.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
            },
            ...SOLUTIONS.map((s) => ({
                "@type": "Service",
                "name": s.title,
                "description": s.description,
                "provider": { "@type": "Organization", "name": "SpringXE" },
                "areaServed": "Global",
            })),
        ],
    }), []);
    return (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
    );
};

/* ============================================================= */
/*                             PAGE                               */
/* ============================================================= */
const Solutions = () => {
    const [active, setActive] = useState(SOLUTIONS[0].slug);
    return (
        <div data-testid="solutions-page">
            <ScrollProgress />
            <Hero />
            <StickyTabs solutions={SOLUTIONS} activeSlug={active} />
            <ImpactMetrics />
            <ProcessTimeline />
            <IndustriesSection />
            <SolutionsIntro />
            {SOLUTIONS.map((s, i) => (
                <div key={s.slug}>
                    <SolutionSection solution={s} index={i} setActive={setActive} />
                    {/* Testimonial after every 2 solution sections (i=1, i=3, i=5) */}
                    {i % 2 === 1 && TESTIMONIALS[(i - 1) / 2] && (
                        <TestimonialBanner t={TESTIMONIALS[(i - 1) / 2]} i={(i - 1) / 2} />
                    )}
                </div>
            ))}
            <TrustedPartners />
            <FAQSection />
            <FinalCTA />
            <SchemaMarkup />
        </div>
    );
};

export default Solutions;
